import Close from "@mui/icons-material/Close";
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { v4 } from "uuid";
import { object, string } from "yup";

import type { CreateTrustRequest, Trust } from "@thalamos/aspire-common";

import { OrganisationPicker } from "../OrganisationPicker.js";
import { api } from "~/api.js";
import {
  Button,
  FormLabel,
  PopupDialog,
  ReadOnlyContent,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";

const trustSchema = object({
  name: string().required(),
  address: string().required(),
  postalCode: string().required(),
  odsCode: string().optional().nullable(),
  emhaOrgId: string().optional().nullable(),
  emhaOrgValue: string().optional().nullable(),
});

export function CreateEditTrustModal({
  trust,
  open,
  onClose,
  refetchTrusts,
}: {
  trust?: Trust;
  open: boolean;
  onClose: () => void;
  refetchTrusts: () => void;
}) {
  const title = trust ? "Edit Trust" : "Create Trust";

  const initialValues: CreateTrustRequest & { emhaOrgText?: string } = {
    name: trust?.name ?? "",
    address: trust?.address ?? "",
    postalCode: trust?.postalCode ?? "",
    odsCode: trust?.odsCode ?? null,
    emhaOrgId: trust?.emhaOrgId ?? null,
    emhaOrgText: trust?.emhaOrgId ?? "",
  };

  return (
    <PopupDialog open={open} onClose={onClose}>
      <DialogTitle>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
        <Box sx={{ my: 1 }}>
          {trust?.id && <ReadOnlyContent content={[trust.id]} />}
        </Box>
      </DialogTitle>

      <Formik<CreateTrustRequest & { emhaOrgText?: string }>
        initialValues={initialValues}
        validationSchema={trustSchema}
        onSubmit={async (values, { setSubmitting }) => {
          const requestData: CreateTrustRequest = {
            name: values.name,
            address: values.address,
            postalCode: values.postalCode,
            odsCode: values.odsCode,
            emhaOrgId: values.emhaOrgId,
          };
          setSubmitting(true);
          try {
            let result;
            if (trust) {
              result = await api.trust.update(trust.id!, requestData);
            } else {
              result = await api.trust.create(v4(), requestData);
            }

            if (result.status === 204) {
              renderSuccessToast({
                message: trust
                  ? "Trust updated successfully."
                  : `Trust "${values.name}" created successfully.`,
              });
            } else {
              renderErrorToast({
                message: trust
                  ? "Failed to update trust."
                  : "Failed to create trust.",
              });
            }
          } catch {
            renderErrorToast({
              message: trust
                ? "Failed to update trust."
                : "Failed to create trust.",
            });
          }
          setSubmitting(false);
          onClose();
          refetchTrusts();
        }}
      >
        {({ values, setValues, errors, touched, submitForm, dirty }) => (
          <>
            <DialogContent>
              <Typography>
                {trust
                  ? "Update trust details below."
                  : "Fill out the details to create a new trust."}
              </Typography>

              <Box sx={{ maxWidth: "600px", mb: 4 }}>
                <FormLabel label="Name" error={touched.name && !!errors.name} />
                <TextField
                  name="name"
                  useFullWidth
                  value={values.name}
                  onChange={(e) => setValues({ ...values, name: e })}
                />

                <FormLabel
                  label="Address"
                  error={touched.address && !!errors.address}
                />
                <TextField
                  name="address"
                  useFullWidth
                  value={values.address}
                  onChange={(e) => setValues({ ...values, address: e })}
                />

                <FormLabel
                  label="Postcode"
                  error={touched.postalCode && !!errors.postalCode}
                />
                <TextField
                  name="postalCode"
                  value={values.postalCode}
                  onChange={(e) => setValues({ ...values, postalCode: e })}
                />

                <FormLabel
                  label="EMHA Organisation ID (optional)"
                  error={touched.emhaOrgId && !!errors.emhaOrgId}
                />
                <OrganisationPicker
                  error={null}
                  idField={"emhaOrgId"}
                  nameField={"emhaOrgText"}
                  values={values}
                  setValues={setValues}
                  label={"Trust"}
                  filterFn={(o) => o.role === "nhs/trust"}
                />

                <FormLabel
                  label="ODS Code (optional)"
                  error={touched.odsCode && !!errors.odsCode}
                />
                <TextField
                  name="odsCode"
                  value={values.odsCode ?? undefined}
                  onChange={(e) => setValues({ ...values, odsCode: e })}
                />
              </Box>
            </DialogContent>

            <DialogActions sx={{ justifyContent: "space-between" }}>
              <Button label="Cancel" onClick={onClose} variant="outlined" />
              <Button label="Save" onClick={submitForm} disabled={!dirty} />
            </DialogActions>
          </>
        )}
      </Formik>
    </PopupDialog>
  );
}
